/**
 * Lightweight super-admin API client.
 * Lives separately from the tenant `api.js` to avoid the existing axios
 * interceptor (which uses `equipr_token` and would conflict).
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'equipr_admin_token';

export const getAdminToken = () => localStorage.getItem(TOKEN_KEY);
export const setAdminToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearAdminToken = () => localStorage.removeItem(TOKEN_KEY);

const authHeaders = () => {
  const t = getAdminToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const adminApi = {
  requestOtp: (email) =>
    axios.post(`${API}/api/admin/otp/request`, { email }),
  verifyOtp: (email, code) =>
    axios.post(`${API}/api/admin/otp/verify`, { email, code }),
  me: () => axios.get(`${API}/api/admin/me`, { headers: authHeaders() }),
  tenants: () => axios.get(`${API}/api/admin/tenants`, { headers: authHeaders() }),
  deleteTenant: (tenantId) =>
    axios.delete(`${API}/api/admin/tenants/${tenantId}`, { headers: authHeaders() }),
  updatePlan: (tenantId, body) =>
    axios.patch(`${API}/api/admin/tenants/${tenantId}/plan`, body, { headers: authHeaders() }),
  // Quick-provision a new tenant. Returns the auto-generated initial password
  // ONCE in the response so the super-admin can hand it to the customer.
  createTenant: (body) =>
    axios.post(`${API}/api/admin/tenants`, body, { headers: authHeaders() }),
  // Set / clear per-tenant Allocate SQL defaults (server + database).
  setAllocateSqlDefaults: (tenantId, body) =>
    axios.patch(`${API}/api/admin/tenants/${tenantId}/allocate-sql-defaults`, body, { headers: authHeaders() }),
  // Telematics gateway observability + self-test
  aiChatLogs: (params) =>
    axios.get(`${API}/api/admin/ai-chat-logs`, { headers: authHeaders(), params }),
  aiKeys: () => axios.get(`${API}/api/admin/equipr-ai-keys`, { headers: authHeaders() }),
  issueAiKey: (body) => axios.post(`${API}/api/admin/equipr-ai-keys`, body, { headers: authHeaders() }),
  revokeAiKey: (keyId) => axios.delete(`${API}/api/admin/equipr-ai-keys/${keyId}`, { headers: authHeaders() }),
  telematicsStatus: () =>
    axios.get(`${API}/api/admin/telematics/gateway-status`, { headers: authHeaders() }),
  telematicsSelfTest: (host, port) =>
    axios.post(`${API}/api/admin/telematics/gateway-test`, null, {
      headers: authHeaders(),
      params: { host: host || undefined, port: port || undefined },
    }),
  // V-DAQ Connect Health — cross-tenant telemetry link status + last sync
  vdaqHealth: () =>
    axios.get(`${API}/api/admin/vdaq/status`, { headers: authHeaders() }),
  scheduledJobs: () =>
    axios.get(`${API}/api/admin/scheduled-jobs`, { headers: authHeaders() }),
  readinessEmailHold: () =>
    axios.get(`${API}/api/admin/readiness-email-hold`, { headers: authHeaders() }),
  startReadinessEmailHold: (reason) =>
    axios.post(`${API}/api/admin/readiness-email-hold`, { reason }, { headers: authHeaders() }),
  endReadinessEmailHold: () =>
    axios.delete(`${API}/api/admin/readiness-email-hold`, { headers: authHeaders() }),
  vdaqSyncNow: () =>
    axios.post(`${API}/api/admin/vdaq/sync-now`, null, { headers: authHeaders() }),
  // Billing overview — MRR/ARR rollup across all tenants
  billingOverview: () =>
    axios.get(`${API}/api/admin/billing/overview`, { headers: authHeaders() }),
  // Audit log feed (super admin — all tenants, optional filters)
  auditLogs: (params = {}) =>
    axios.get(`${API}/api/admin/audit-logs`, { headers: authHeaders(), params }),
  // Broadcasts — templates + send
  broadcastTemplates: () =>
    axios.get(`${API}/api/admin/broadcast/templates`, { headers: authHeaders() }),
  broadcastSaveTemplate: (body) =>
    axios.post(`${API}/api/admin/broadcast/templates`, body, { headers: authHeaders() }),
  broadcastDeleteTemplate: (slug) =>
    axios.delete(`${API}/api/admin/broadcast/templates/${slug}`, { headers: authHeaders() }),
  broadcastAudiencePreview: (body) =>
    axios.post(`${API}/api/admin/broadcast/audience-preview`, body, { headers: authHeaders() }),
  broadcastSend: (body) =>
    axios.post(`${API}/api/admin/broadcast/send`, body, { headers: authHeaders() }),
  broadcastHistory: () =>
    axios.get(`${API}/api/admin/broadcast/history`, { headers: authHeaders() }),
  // Lifecycle email analytics (cross-tenant)
  lifecycleStats: () =>
    axios.get(`${API}/api/admin/lifecycle/stats`, { headers: authHeaders() }),
  lifecycleSends: (params = {}) =>
    axios.get(`${API}/api/admin/lifecycle/sends`, { headers: authHeaders(), params }),
  lifecycleRunNow: () =>
    axios.post(`${API}/api/admin/lifecycle/run-now`, null, { headers: authHeaders() }),
};
